import { Injectable, inject } from '@angular/core';

import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, switchMap } from 'rxjs';
import { tap, take } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { createEffectFactory } from '@core/ngrx-legacy';
import type { SupportedThemesT, ThemeName } from '@core/theme-util';
import { RECRUITEE_THEME_LS_KEY } from '@core/theme-util';

import { CurrentUserPreferencesHttpClient } from './current-user-preferences-http-client';
import {
  CurrentUserPreferencesActions,
  CurrentUserPreferencesSelectors,
} from './current-user-preferences.store';

@Injectable({ providedIn: 'root' })
export class CurrentUserPreferencesEffects {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly currentUserPreferencesService = inject(CurrentUserPreferencesHttpClient);

  public effects = createEffectFactory(CurrentUserPreferencesActions, this.actions$);

  public fetchCurrentUserPreferences$ = this.effects(CurrentUserPreferencesActions.fetch, () =>
    this.currentUserPreferencesService
      .loadCurrentUserPreferences()
      .pipe(tap(data => this.cacheTheme(data.preferences.theme))),
  );

  public updatePreferredTheme$ = this.effects(
    CurrentUserPreferencesActions.updatePreferredTheme,
    ({ payload }) => {
      const themeName = <SupportedThemesT>payload;
      this.cacheTheme(themeName);

      return this.store.select(CurrentUserPreferencesSelectors.data).pipe(
        filterBoolean,
        take(1),
        switchMap(data =>
          of(
            CurrentUserPreferencesActions.update({
              payload: {
                ...data,
                preferences: {
                  ...data.preferences,
                  theme: themeName,
                },
              },
            }),
          ),
        ),
      );
    },
  );

  public updatePipelinePromoteBannerDismissedOfferIds$ = this.effects(
    CurrentUserPreferencesActions.updatePipelinePromoteBannerDismissedOfferIds,
    ({ payload }) =>
      this.store.select(CurrentUserPreferencesSelectors.data).pipe(
        filterBoolean,
        take(1),
        switchMap(data =>
          of(
            CurrentUserPreferencesActions.update({
              payload: {
                ...data,
                preferences: {
                  ...data.preferences,
                  pipelinePromoteBannerDismissedOfferIds: payload,
                },
              },
            }),
          ),
        ),
      ),
  );

  public update$ = this.effects(CurrentUserPreferencesActions.update, ({ payload }) =>
    this.currentUserPreferencesService.updateCurrentUserPreferences(payload),
  );

  private cacheTheme(theme: ThemeName): void {
    localStorage.setItem(RECRUITEE_THEME_LS_KEY, theme);
  }
}
